import { Modal, Row, Col } from "antd";
import { Box, Flex, Textarea, Text } from "@chakra-ui/react";
import SelectUnidade from "components/Input/SelectUnidade";
import OutlineButton from "ui/components/Button/OutlineButton";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import Field from "ui/components/Input/Field";
import { useForm } from "react-hook-form";
import { showSuccess, showErrorAxios } from "infra/helpers/alerta";
import useSaveEncomenda from "core/features/encomenda/hooks/useSaveEncomenda";
import SelectPessoas from "ui/components/Input/SelectPessoas";
import { TipoEntidadeEnum } from "core/resources/enums/conta";
import { EncomendaForm } from "core/features/encomenda/typings";
import { useEffect } from "react";
import Anexos from "ui/components/Anexos";
import ButtonAnexarArquivos from "ui/components/Button/ButtonAnexarArquivos";
import SelectGrupoUnidade from "components/Input/SelectGrupoUnidade";

interface ModalCadastroEncomendaProps {
  visible: boolean;
  onClose: () => any;
  onSaved: () => any;
  refetchList?: () => any;
}

export default function ModalCadastrarEncomenda({
  onSaved,
  visible,
  onClose,
  refetchList,
}: ModalCadastroEncomendaProps) {
  const { handleSubmit, control, setValue, watch, reset } =
    useForm<EncomendaForm>({});

  const { mutate: handleSave, isLoading } = useSaveEncomenda();

  const onSubmit = async (data: EncomendaForm) => {
    handleSave(data, {
      onSuccess: () => {
        setTimeout(() => {
          showSuccess().then(() => onSaved());
        }, 250);
        refetchList?.();
      },
      onError: (err) => {
        showErrorAxios(err);
      },
    });
  };

  useEffect(() => {
    if (!visible) {
      reset({});
    }
  }, [reset, visible]);

  const unidadeId = watch("unidadeId");
  const form = watch();
  return (
    <Modal
      destroyOnClose
      width={"600px"}
      onCancel={onClose}
      centered
      title="Cadastrar nova encomenda"
      visible={visible}
      footer={
        <Flex justifyContent="end" gap={4}>
          <OutlineButton onClick={onClose}> Fechar</OutlineButton>
          <PrimaryButton
            isLoading={isLoading}
            onClick={() => handleSubmit(onSubmit)()}
          >
            Salvar
          </PrimaryButton>
        </Flex>
      }
    >
      <form onSubmit={(e) => handleSubmit(onSubmit)(e)}>
        <Box as={Row} gutter={16} mb={4}>
          <Col md={12}>
            <Field
              shouldUnregister
              label="Filtrar por grupo"
              control={control}
              name="gruposUnidadesIds"
              render={({ field, isInvalid }) => (
                <SelectGrupoUnidade
                  allowClear
                  mode="multiple"
                  isInvalid={isInvalid}
                  {...field}
                />
              )}
            />
          </Col>

          <Col md={12}>
            <Field
              shouldUnregister
              label="Unidade"
              control={control}
              name="unidadeId"
              rules={{ required: "Campo obrigatório" }}
              render={({ field, isInvalid }) => (
                <SelectUnidade
                  includeGruposUnidadesLabel={false}
                  isInvalid={isInvalid}
                  {...field}
                  gruposUnidadesIds={
                    form.gruposUnidadesIds?.length
                      ? form.gruposUnidadesIds
                      : undefined
                  }
                />
              )}
            />
          </Col>
        </Box>

        <Box mb={4}>
          <Field
            shouldUnregister
            label="Morador"
            control={control}
            name="pessoaId"
            rules={{ required: "Campo obrigatório" }}
            render={({ field, isInvalid }) => (
              <SelectPessoas
                {...field}
                unidadeId={unidadeId!}
                isInvalid={isInvalid}
                tipoEntidades={[
                  TipoEntidadeEnum.Condomino,
                  TipoEntidadeEnum.Funcionario,
                  TipoEntidadeEnum.Outros,
                ]}
              />
            )}
          />
        </Box>

        <Box mb={4}>
          <Field
            label="Descrição"
            name="descricao"
            rules={{ required: "Campo obrigatório" }}
            control={control}
          />
        </Box>

        <Box mb={4}>
          <Field
            label="Anotações"
            name="observacao"
            inputProps={{ type: "text" }}
            control={control}
            render={({ field, isInvalid }) => (
              <Textarea
                isInvalid={isInvalid}
                className="chakra-input"
                rows={4}
                placeholder="Digite..."
                {...field}
              />
            )}
          />
        </Box>

        <Box>
          <Text mb="2px" fontSize="13px">
            Arquivos
          </Text>
          <Flex gap="16px" mt="auto" h="fit-content" py={2} flexDir="column">
            {form.files?.length ? (
              <Anexos
                numColumns={2}
                setValue={(anexos) => setValue("files", anexos as File[])}
                anexos={form.files || undefined}
              />
            ) : null}
          </Flex>
          <Flex flex={1}>
            <ButtonAnexarArquivos
              maxFileSizeMb={20}
              currentFilesLength={form.files?.length || 0}
              variant="ghost"
              onAnexar={(files) => {
                if (files) {
                  const currentAnexos = form.files;

                  setValue(
                    "files",
                    currentAnexos ? [...currentAnexos, ...files] : files
                  );
                }
              }}
            />
          </Flex>
        </Box>
      </form>
    </Modal>
  );
}
